import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import useProfile from "api/UserResource/useProfile";
import { Link } from "react-router-dom";
import { MdOutlineSubscriptions } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import Moment from "react-moment";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import AccountLayout from "../AccountLayout";
import AddSeatsModal from "components/AddSeatsModal";
import { useState } from "react";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";

const MySubscription = () => {
	const today = new Date();
	const [showAddSeatsModal, setShowAddSeatsModal] = useState(false);
	const hasRoles = useAuthStore((state) => state.hasRoles);

	const { data: isSubscribed, isLoading: isLoadingSubscriptionPackage } =
		useCheckIfUserIsSubscribed();

	const { data: profile, isLoading: isLoadingProfile } = useProfile();

	const currentSubscription = isSubscribed?.data || null;

	const isInstitutional = isSubscribed?.data?.category === "institution";
	const canAddSeats = isInstitutional && hasRoles([Role.Institution, Role.Educator]);
	const seatsPurchased = currentSubscription?.seats_purchased || currentSubscription?.number_of_seats || 1;
	const maxAllocationsPerSeat =
		currentSubscription?.max_allocations_per_seat || currentSubscription?.max_number_of_books || 0;
	const picksCount =
		currentSubscription?.picks_count ?? currentSubscription?.current_premium_books_added ?? 0;
	const picksPolicy = currentSubscription?.picks_policy || (isInstitutional ? "unlimited" : "limited");
	const remainingPicks =
		currentSubscription?.remaining_picks ?? currentSubscription?.remaining ?? 0;

	return (
		<Container>
			<div className="min-h-screen bg-gray-50">
				<div className="pt-6 pb-12">
					<PaddingX>
						<AccountLayout
							isLoading={isLoadingProfile || isLoadingSubscriptionPackage}
							profile={profile?.data}
						>
							{/* Header Section */}
							<div className="mb-8">
								<h1 className="text-2xl font-semibold mb-2 ">My Subscription</h1>
								<p className="text-gray-500 leading-relaxed">
									{isInstitutional
										? "Manage your institution plan, purchased seats, and allocation capacity."
										: "Manage your subscription plan and track your reading picks. Upgrade or renew your plan anytime."}
								</p>
							</div>

							{/* Subscription Content */}
							{(function () {
								if (!currentSubscription) {
									return (
										<div className="text-center py-12">
											<div className="max-w-md mx-auto">
												<div className="w-32 h-32 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
													<MdOutlineSubscriptions size={64} className="text-gray-400" />
												</div>
												<h3 className="text-xl font-semibold text-gray-900 mb-3">
													No Active Subscription
												</h3>
												<p className="text-gray-500 mb-8 leading-relaxed">
													You currently don't have an active subscription plan. Subscribe now to
													access premium books and features.
												</p>
												<Link
													to="/subscriptions"
													className="inline-flex items-center gap-2 px-6 py-3 bg-akgreener text-white font-medium rounded-lg hover:bg-akgreen focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
												>
													<svg
														className="w-5 h-5"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M12 6v6m0 0v6m0-6h6m-6 0H6"
														/>
													</svg>
													View Subscription Plans
												</Link>
											</div>
										</div>
									);
								}

								return (
									<div className="space-y-6">
										{/* Subscription Status Card */}
										<div className="bg-white border border-gray-200 rounded-lg p-6">
											<div className="flex items-center justify-between mb-4">
												<div>
													<h3 className="text-xl font-bold mb-1 text-gray-900">
														{currentSubscription?.name}
													</h3>
													<p className="text-gray-500 text-sm">Active Subscription</p>
												</div>
												<div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
													<svg
														className="w-6 h-6 text-gray-600"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M5 13l4 4L19 7"
														/>
													</svg>
												</div>
											</div>
											<div className="flex items-center gap-4 text-sm">
												<div>
													<span className="text-gray-500">Expires</span>
													<div className="font-medium text-gray-900">
														<Moment to={currentSubscription?.expiry_date}>{today}</Moment>
													</div>
												</div>
											</div>
										</div>

										{/* Institutional Subscription - Seats Info */}
										{isInstitutional && (
											<div className="bg-white border border-gray-200 rounded-lg p-6">
												<div className="flex items-center justify-between mb-4">
													<div>
														<h4 className="font-semibold text-gray-900 mb-1">Institution Seats</h4>
														<p className="text-gray-500 text-sm">
															Number of students / seats purchased under this plan
														</p>
													</div>
													{canAddSeats && (
														<button
															onClick={() => setShowAddSeatsModal(true)}
															className="px-4 py-2 bg-gradient-to-r from-akgreen to-akgreener text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
														>
															<FiPlus size={18} />
															Add Seats
														</button>
													)}
												</div>
												<div className="text-3xl font-bold text-akgreen">
													{seatsPurchased}
												</div>
											</div>
										)}

										{/* Reading Stats */}
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<div className="bg-white rounded-lg p-6 border border-gray-200">
												<div className="flex items-center gap-3 mb-3">
													<div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
														<svg
															className="w-5 h-5 text-gray-600"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
															/>
														</svg>
													</div>
													<h4 className="font-medium text-gray-900">
														{isInstitutional ? "Seats Purchased" : "Total Picks"}
													</h4>
												</div>
												<p className="text-2xl font-bold text-gray-900">
													{isInstitutional ? seatsPurchased : currentSubscription?.max_number_of_books}
												</p>
											</div>

											<div className="bg-white rounded-lg p-6 border border-gray-200">
												<div className="flex items-center gap-3 mb-3">
													<div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
														<svg
															className="w-5 h-5 text-gray-600"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
															/>
														</svg>
													</div>
													<h4 className="font-medium text-gray-900">
														{isInstitutional ? "Allocations per Seat" : "Picks Used"}
													</h4>
												</div>
												<p className="text-2xl font-bold text-gray-900">
													{isInstitutional
														? maxAllocationsPerSeat
														: currentSubscription?.current_premium_books_added}
												</p>
											</div>

											<div className="bg-white rounded-lg p-6 border border-gray-200">
												<div className="flex items-center gap-3 mb-3">
													<div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
														<svg
															className="w-5 h-5 text-gray-600"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
															/>
														</svg>
													</div>
													<h4 className="font-medium text-gray-900">
														{isInstitutional ? "Picks Bank" : "Remaining"}
													</h4>
												</div>
												<p className="text-2xl font-bold text-gray-900">
													{isInstitutional ? (picksPolicy === "unlimited" ? "Unlimited" : remainingPicks) : remainingPicks}
												</p>
												{isInstitutional && (
													<p className="mt-2 text-sm text-gray-500">
														{picksCount} book{picksCount === 1 ? "" : "s"} currently in the picks bank
													</p>
												)}
											</div>
										</div>

										{/* Information Card */}
										<div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
											<div className="flex items-start gap-3">
												<div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
													<svg
														className="w-5 h-5 text-gray-600"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
														/>
													</svg>
												</div>
												<div>
													<h4 className="font-medium text-gray-900 mb-2">
														{isInstitutional ? "How institution access works" : "About Reading Picks"}
													</h4>
													{isInstitutional ? (
														<p className="text-sm text-gray-600">
															Your institution plan lets you buy seats for students. Each student can
															receive up to <span className="font-medium">{maxAllocationsPerSeat}</span>{" "}
															books, and each picked book can be allocated to up to{" "}
															<span className="font-medium">{seatsPurchased}</span> unique students
															before you need to top up seats.
														</p>
													) : (
														<p className="text-sm text-gray-600">
															Under your current active subscription,{" "}
															<span className="font-medium">picks</span> represent the number of books
															you can add to your library for reading. Each pick allows you to access
															one premium book.
														</p>
													)}
												</div>
											</div>
										</div>

										{/* Action Buttons */}
										<div className="flex flex-col sm:flex-row gap-4">
											<Link
												to="/subscriptions"
												className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-center"
											>
												Other Subscription Plans
											</Link>
											<Link
												to="/my-library"
												className="flex-1 px-6 py-3 bg-akgreener text-white font-medium rounded-lg hover:bg-akgreen focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 text-center"
											>
												View My Library
											</Link>
										</div>
									</div>
								);
							})()}

							{/* Add Seats Modal */}
							{canAddSeats && (
								<AddSeatsModal
									visible={showAddSeatsModal}
									onHide={() => setShowAddSeatsModal(false)}
									subscription={currentSubscription}
								/>
							)}
						</AccountLayout>
					</PaddingX>
				</div>
			</div>
		</Container>
	);
};

export default MySubscription;
